import { useMemo } from 'react'
import { C, colHead } from '../theme'
import { grp, localTime, signedUsd, usd } from '../lib/format'
import { Chip, Note, SectionHead, Strong } from './ui/primitives'
import { SOLVERS } from '../data/mock/constants'
import { useOtherQuotes } from '../data/liveQuotesOther'
import type { QuoteRow, Snapshot, Solver } from '../types'

export type FeedKind = 'quotes' | 'trades'

const Q_GRID = '96px 40px minmax(0,1.3fr) minmax(0,1.2fr) 1fr 84px 78px minmax(0,1.6fr)'
const T_GRID = '96px 1.3fr 1fr 1fr 1fr 1.1fr 1.2fr'

function outcomeColor(outcome: string, skipped: boolean): string {
  if (skipped) return C.warn
  if (outcome === 'settled') return C.pos
  if (outcome === 'quote past deadline') return C.neg
  if (outcome.startsWith('no rejection')) return C.warn
  return C.muted2
}

function downloadCsv(rows: QuoteRow[]) {
  const head = 'time,algo_index,direction,solver,size_usd,edge_bps,latency_ms,outcome\n'
  const body = rows
    .map((q) =>
      [
        localTime(q.tsMs),
        q.index,
        q.direction,
        q.solver,
        q.notionalUsd.toFixed(2),
        q.edgeBps.toFixed(2),
        q.latencyMs.toFixed(0),
        q.outcome,
      ].join(','),
    )
    .join('\n')
  const a = document.createElement('a')
  a.href = URL.createObjectURL(new Blob([head + body], { type: 'text/csv' }))
  a.download = 'quote-feed.csv'
  a.click()
}

export function Feeds({
  snap,
  feed,
  onFeed,
  solverFilter,
  onSolverFilter,
  showNotes,
}: {
  snap: Snapshot
  feed: FeedKind
  onFeed: (f: FeedKind) => void
  solverFilter: Solver | null
  onSolverFilter: (s: Solver | null) => void
  showNotes: boolean
}) {
  const otherQuotes = useOtherQuotes()

  const merged = useMemo(() => {
    const all = [...snap.quotes, ...otherQuotes]
    all.sort((a, b) => b.tsMs - a.tsMs)
    return all
  }, [snap.quotes, otherQuotes])

  const quotes = useMemo(
    () => merged.filter((q) => !solverFilter || q.solver === solverFilter),
    [merged, solverFilter],
  )

  const ours = snap.trades.filter((t) => t.run === 'this').length

  return (
    <>
      <SectionHead
        title="Feeds"
        right={
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            <Chip active={feed === 'quotes'} pad="3px 10px" onClick={() => onFeed('quotes')}>
              quote feed
            </Chip>
            <Chip active={feed === 'trades'} pad="3px 10px" onClick={() => onFeed('trades')}>
              trades
            </Chip>
          </span>
        }
      />

      {feed === 'quotes' && (
        <div style={{ marginTop: 6 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              rowGap: 6,
              padding: '10px 0',
            }}
          >
            <span style={{ fontSize: 14, color: C.muted2 }}>
              {grp(quotes.length)} of {grp(snap.quotesTotal)} shown · ring buffer{' '}
              {grp(snap.ringBufferSize)}
            </span>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
              }}
            >
              <Chip active={solverFilter === null} onClick={() => onSolverFilter(null)}>
                all
              </Chip>
              {SOLVERS.map((s) => (
                <Chip
                  key={s}
                  active={solverFilter === s}
                  onClick={() => onSolverFilter(solverFilter === s ? null : s)}
                >
                  {s.replace('_integration', '')}
                </Chip>
              ))}
              <span
                onClick={() => downloadCsv(quotes)}
                style={{
                  cursor: 'pointer',
                  fontSize: 13,
                  padding: '2px 9px',
                  border: `1px solid ${C.accentTint}`,
                  color: C.accent,
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
              >
                CSV
              </span>
            </span>
          </div>

          <div data-xscroll>
            <div data-wide>
              <div
                style={{
                  ...colHead,
                  display: 'grid',
                  gridTemplateColumns: Q_GRID,
                  gap: 12,
                  borderBottom: `1px solid ${C.border}`,
                  paddingBottom: 8,
                }}
              >
                <span>Time</span>
                <span>Algo</span>
                <span>Direction</span>
                <span>Solver</span>
                <span style={{ textAlign: 'right' }}>Size</span>
                <span style={{ textAlign: 'right' }}>Edge</span>
                <span style={{ textAlign: 'right' }}>Lat</span>
                <span>Outcome</span>
              </div>
              {quotes.length === 0 ? (
                <div style={{ fontSize: 15, color: C.muted2, padding: '14px 0' }}>
                  0 of {grp(snap.quotesTotal)} ·{' '}
                  <span
                    onClick={() => onSolverFilter(null)}
                    style={{ color: C.accent, cursor: 'pointer' }}
                  >
                    clear filters
                  </span>
                </div>
              ) : (
                quotes.map((q, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: Q_GRID,
                      gap: 12,
                      alignItems: 'baseline',
                      padding: '10px 0',
                      borderBottom: `1px solid ${C.rule}`,
                      fontSize: 15,
                      whiteSpace: 'nowrap',
                      background: q.skipped ? C.skipWash : 'transparent',
                    }}
                  >
                    <span style={{ color: C.muted, fontSize: 14 }}>{localTime(q.tsMs)}</span>
                    <span style={{ color: C.muted2, fontSize: 14 }}>{q.index}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.direction}</span>
                    <span style={{ color: C.muted2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {q.solver}
                      {q.dedupBadge && (
                        <span style={{ color: C.accent, fontSize: 13, paddingLeft: 6 }}>
                          {q.dedupBadge}
                        </span>
                      )}
                    </span>
                    <span style={{ textAlign: 'right' }}>{usd(q.notionalUsd)}</span>
                    <span style={{ textAlign: 'right', color: q.edgeBps >= 0 ? C.pos : C.neg }}>
                      {(q.edgeBps >= 0 ? '+' : '') + q.edgeBps.toFixed(2)}
                    </span>
                    <span
                      style={{
                        textAlign: 'right',
                        color: q.latencyMs > 750 ? C.neg : q.latencyMs > 250 ? C.warn : C.muted2,
                      }}
                    >
                      {q.latencyMs.toFixed(0)} ms
                    </span>
                    <span
                      style={{
                        color: outcomeColor(q.outcome, q.skipped),
                        fontSize: 14,
                        whiteSpace: 'normal',
                        lineHeight: 1.3,
                      }}
                    >
                      {q.outcome}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {showNotes && (
            <Note>
              Bursts above eight events per second from one solver in one direction collapse into a
              single row, and retries dedupe to ×N — 53 RFQ in one second would otherwise be 53
              identical lines. <Strong>no rejection observed</Strong> is a ten-second observation
              timeout: neither a win nor a loss, because losing is silence. The reason on a skipped
              row is an exception class name today, not a stable code.
            </Note>
          )}
        </div>
      )}

      {feed === 'trades' && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 14, color: C.muted2, padding: '10px 0' }}>
            {snap.trades.length} settlements shown · {ours} of them by this runner
          </div>
          <div data-xscroll>
            <div data-wide>
              <div
                style={{
                  ...colHead,
                  display: 'grid',
                  gridTemplateColumns: T_GRID,
                  gap: 12,
                  borderBottom: `1px solid ${C.border}`,
                  paddingBottom: 8,
                }}
              >
                <span>Time</span>
                <span>Direction</span>
                <span style={{ textAlign: 'right' }}>Size</span>
                <span style={{ textAlign: 'right' }}>Gross</span>
                <span style={{ textAlign: 'right' }}>Net</span>
                <span style={{ textAlign: 'right' }}>Block</span>
                <span>Attribution</span>
              </div>
              {snap.trades.map((t, i) => (
                <div
                  key={i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: T_GRID,
                    gap: 12,
                    alignItems: 'baseline',
                    padding: '10px 0',
                    borderBottom: `1px solid ${C.rule}`,
                    fontSize: 15,
                    whiteSpace: 'nowrap',
                    background: t.run === 'prev' ? C.panel : 'transparent',
                  }}
                >
                  <span style={{ color: C.muted, fontSize: 14 }}>{localTime(t.tsMs)}</span>
                  <span
                    style={{
                      color: t.run === 'prev' ? C.muted2 : C.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {t.direction.replace('->', '→')}
                    {t.roundTrip && (
                      <span
                        title="round-trip leg — an arbitrageur taking our discount twice, not demand"
                        style={{ color: C.accent, fontSize: 13, paddingLeft: 6 }}
                      >
                        ⇄
                      </span>
                    )}
                  </span>
                  <span style={{ textAlign: 'right' }}>{usd(t.notionalUsd)}</span>
                  <span style={{ textAlign: 'right', color: C.pos }}>
                    {signedUsd(t.grossUsd, 2)}
                  </span>
                  <span
                    style={{
                      textAlign: 'right',
                      fontWeight: 600,
                      color: t.netUsd >= 0 ? C.pos : C.neg,
                    }}
                  >
                    {signedUsd(t.netUsd, 2)}
                  </span>
                  <span style={{ textAlign: 'right', color: C.muted, fontSize: 14 }}>
                    {t.blockLabel}
                  </span>
                  <span
                    style={{
                      color: t.attribution === 'ambiguous' ? C.warn : C.pos,
                      fontSize: 14,
                    }}
                  >
                    {t.attribution}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {showNotes && (
            <Note>
              The venue sends <Strong>no settlement notifications</Strong> — its own fill counter is
              identically zero — so fills are found by scanning the chain for keccak of the rfq id.
              The maker address is shared with the legacy bot: a trade is ours when run id, rfq id
              and the price to the seventh digit all agree, otherwise it stays{' '}
              <span style={{ color: C.warn }}>ambiguous</span>. A ⇄ pair is one arbitrageur's
              round-trip — turnover, not demand.
            </Note>
          )}
        </div>
      )}
    </>
  )
}
